import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/ca';
import { uiActualizarResultatsEvaluacioAction } from '../../../actions/ui';
import { useNavigate } from 'react-router-dom';
import { recuperarHistorialAdminPaginat } from '../../../actions/configAppAction';
import { ConclusionesAdmin } from './ConclusionesAdmin';

// ─── Constantes ───────────────────────────────────────────────────────────────
const CAT_COLORS = [
  'var(--color-1)',
  'var(--color-2)',
  'var(--color-3)',
  'var(--color-4)',
  'var(--color-5)',
  'var(--color-6)',
  'var(--color-7)',
  'var(--color-8)',
  'var(--color-9)',
];const POR_PAGINA = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) => (val == null ? '—' : (val % 1 === 0 ? String(val) : (val * 1).toFixed(2)));

// ─── Score badge ──────────────────────────────────────────────────────────────
const ScoreBadge = ({ value }) => {
    if (value === '—') return <span style={{ color: '#d1d5db' }}>—</span>;
    const n     = parseFloat(value);
const color =
  n >= 70
    ? 'var(--color-success)'
    : n >= 40
    ? 'var(--color-warning)'
    : 'var(--color-danger)';    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 20,
            fontSize: 12, fontWeight: 700,
            backgroundColor: color + '18', color, border: `1px solid ${color}44`,
        }}>
            {value}%
        </span>
    );
};

// ─── Skeleton row ─────────────────────────────────────────────────────────────
const SkeletonRow = ({ cols }) => (
    <tr>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} style={{ padding: '14px 12px', borderBottom: '1px solid #f9fafb' }}>
                <div className="ca-skeleton-cell" style={{
                    width: i === 0 ? '80%' : i === 1 ? '60%' : '40%',
                }} />
            </td>
        ))}
    </tr>
);

// ─── Icono de ordenación ──────────────────────────────────────────────────────
const SortIcon = ({ col, ordenCol, ordenDir }) => {
    if (ordenCol !== col) return <span style={{ opacity: 0.25, marginLeft: 3, fontSize: 10 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: '#6f9b19', fontSize: 10 }}>{ordenDir === 'ASC' ? '↑' : '↓'}</span>;
};

export const CercaTableADMIN = () => {
    const dispatch  = useDispatch();
    const navegacio = useNavigate();
    const [t]       = useTranslation("generic");
    const { auth, configApp } = useSelector(state => state);

    // ── Estado ────────────────────────────────────────────────────────────────
    const [pagina,           setPagina]           = useState(1);
    const [busqueda,         setBusqueda]         = useState('');
    const [busquedaInput,    setBusquedaInput]    = useState('');
    const [filtroPendientes, setFiltroPendientes] = useState(false);
    const [ordenCol,         setOrdenCol]         = useState('DataFin_Evaluacion');
    const [ordenDir,         setOrdenDir]         = useState('DESC');

    const [conclusionesOpen,       setConclusionesOpen]       = useState(false);
    const [evaluacionSeleccionada, setEvaluacionSeleccionada] = useState(null);
    const [conclusionesMap,        setConclusionesMap]        = useState({});

    const { historialAdmin = {}, carregant = false } = configApp;
    const { contingut = [], total = 0, totalSinConcl = 0 } = historialAdmin;
    const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchDades = useCallback(() => {
        dispatch(recuperarHistorialAdminPaginat(auth.tokenJwt, {
            pagina, porPagina: POR_PAGINA,
            busqueda, soloPendientes: filtroPendientes,
            ordenCol, ordenDir,
        }));
    }, [dispatch, auth.tokenJwt, pagina, busqueda, filtroPendientes, ordenCol, ordenDir]);

    useEffect(() => { fetchDades(); }, [fetchDades]);

    // ── Debounce buscador ─────────────────────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => { setBusqueda(busquedaInput); setPagina(1); }, 300);
        return () => clearTimeout(timer);
    }, [busquedaInput]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleSort = (col) => {
        if (ordenCol === col) setOrdenDir(d => d === 'ASC' ? 'DESC' : 'ASC');
        else { setOrdenCol(col); setOrdenDir('ASC'); }
        setPagina(1);
    };

    const handleFiltroPendientes = () => { setFiltroPendientes(f => !f); setPagina(1); };

    const handleAbrirConclusiones = (e, row) => {
        e.stopPropagation();
        setEvaluacionSeleccionada(row);
        setConclusionesOpen(true);
    };

    const handleConclusionesGuardadas = (idEvaluacion, texto) => {
        setConclusionesMap(prev => ({ ...prev, [idEvaluacion]: texto }));
        fetchDades();
    };

    const handleRowClick = (row) => {
        dispatch(uiActualizarResultatsEvaluacioAction({
            IdEvaluacion:        row.IdEvaluacion,
            Nombre_Mun:          row.Nombre_Mun,
            DataFin_Evaluacion:  row.fechaRaw,
            ResultatGlobal:      row.ResultatGlobalRaw,
            Resultado_TotalCat1: row.catsRaw[0],
            Resultado_TotalCat2: row.catsRaw[1],
            Resultado_TotalCat3: row.catsRaw[2],
            Resultado_TotalCat4: row.catsRaw[3],
            Resultado_TotalCat5: row.catsRaw[4],
            Resultado_TotalCat6: row.catsRaw[5],
            Resultado_TotalCat7: row.catsRaw[6],
            Resultado_TotalCat8: row.catsRaw[7],
            Resultado_TotalCat9: row.catsRaw[8],
        }));
        navegacio('/resultats');
    };

    // ── Preparar filas ────────────────────────────────────────────────────────
    const filas = contingut.map((item) => {
        const id = item.IdEvaluacion;
        const tieneConcl = conclusionesMap[id] != null
            ? conclusionesMap[id].trim().length > 0
            : (item.Conclusiones ? item.Conclusiones.trim().length > 0 : false);
        return {
            IdEvaluacion:      id,
            Nombre_Mun:        item.Nombre_Mun,
            fecha:             moment(item.DataFin_Evaluacion).format('YYYY-MM-DD'),
            fechaRaw:          item.DataFin_Evaluacion,
            ResultatGlobalRaw: item.ResultatGlobal,
            catsRaw: [
                item.Resultado_TotalCat1, item.Resultado_TotalCat2, item.Resultado_TotalCat3,
                item.Resultado_TotalCat4, item.Resultado_TotalCat5, item.Resultado_TotalCat6,
                item.Resultado_TotalCat7, item.Resultado_TotalCat8, item.Resultado_TotalCat9,
            ],
            ResultatGlobal: fmt(item.ResultatGlobal),
            cats: [1,2,3,4,5,6,7,8,9].map(n => fmt(item[`Resultado_TotalCat${n}`])),
            tieneConclusiones: tieneConcl,
            conclusionesTexto: conclusionesMap[id] ?? (item.Conclusiones || ''),
        };
    });

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

                {/* ── Cabecera ─────────────────────────────────────────────── */}
                <div className="ca-toolbar" style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', justifyContent: 'space-between' }}>
                    <div>
                        <p className="uppercase font-medium text-sm tracking-[0.84px] after:bg-green-semilight after:absolute after:w-full after:h-2 relative after:left-0 inline-block after:bottom-0 after:-z-10">
                            Historial de evaluaciones
                        </p>
                        <p className="ca-subtext" style={{ marginTop: 4 }}>
                            {carregant
                                ? 'Cargando...'
                                : filtroPendientes
                                    ? `${total} sin conclusiones`
                                    : `${total} evaluaciones`}
                        </p>
                    </div>

                    <div className="ca-toolbar">
                        {/* Buscador */}
                        <div className="ca-search-wrap">
                            <span className="ca-search-icon">🔍</span>
                            <input
                                type="text"
                                placeholder="Buscar municipio..."
                                value={busquedaInput}
                                onChange={e => setBusquedaInput(e.target.value)}
                                className="ca-search-input"
                            />
                        </div>

                        {/* Botón filtro pendientes */}
                        <button
                            onClick={handleFiltroPendientes}
                            className={`ca-btn-filter${filtroPendientes ? ' active' : ''}`}
                        >
                            <span>{filtroPendientes ? '✕' : '⚠️'}</span>
                            <span>{filtroPendientes ? 'Ver todas' : 'Sin conclusiones'}</span>
                            {totalSinConcl > 0 && (
                                <span className="ca-badge-count" style={{
                                    backgroundColor: filtroPendientes ? '#e20078' : '#fce7f3',
                                    color: filtroPendientes ? '#fff' : '#e20078',
                                }}>
                                    {totalSinConcl}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Tabla ────────────────────────────────────────────────── */}
                <div className="ca-table-wrap">
                    <table className="ca-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('Nombre_Mun')}>
                                    Municipio <SortIcon col="Nombre_Mun" ordenCol={ordenCol} ordenDir={ordenDir} />
                                </th>
                                <th onClick={() => handleSort('DataFin_Evaluacion')}>
                                    Fecha fin <SortIcon col="DataFin_Evaluacion" ordenCol={ordenCol} ordenDir={ordenDir} />
                                </th>
                                <th style={{ textAlign: 'center' }} onClick={() => handleSort('ResultatGlobal')}>
                                    Global <SortIcon col="ResultatGlobal" ordenCol={ordenCol} ordenDir={ordenDir} />
                                </th>
                                {CAT_COLORS.map((color, i) => (
                                    <th key={i} style={{ textAlign: 'center', padding: '10px 8px' }}>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 22, height: 22, borderRadius: '50%',
                                            backgroundColor: color, color: '#fff',
                                            fontSize: 10, fontWeight: 700,
                                        }}>
                                            {i + 1}
                                        </span>
                                    </th>
                                ))}
                                <th style={{ textAlign: 'center', minWidth: 170 }}>Conclusiones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carregant ? (
                                Array.from({ length: POR_PAGINA }).map((_, i) => (
                                    <SkeletonRow key={i} cols={13} />
                                ))
                            ) : filas.length === 0 ? (
                                <tr>
                                    <td colSpan={13} style={{ textAlign: 'center', padding: '56px 24px', color: '#9ca3af' }}>
                                        {filtroPendientes
                                            ? <><div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div><div style={{ fontWeight: 600 }}>¡Todas las evaluaciones tienen conclusiones!</div></>
                                            : <><div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div><div>No se encontraron evaluaciones.</div></>
                                        }
                                    </td>
                                </tr>
                            ) : filas.map((row) => (
                                <tr
                                    key={row.IdEvaluacion}
                                    onClick={() => handleRowClick(row)}
                                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                                >
                                    <td style={{ fontWeight: 600, color: '#111827' }}>{row.Nombre_Mun}</td>
                                    <td style={{ color: '#6b7280' }}>{row.fecha}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        <ScoreBadge value={row.ResultatGlobal} />
                                    </td>
                                    {row.cats.map((v, i) => (
                                        <td key={i} style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '11px 8px' }}>
                                            {v === '—' ? <span style={{ color: '#e5e7eb' }}>—</span> : `${v}%`}
                                        </td>
                                    ))}
                                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                                            {/* Badge estado */}
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                                                backgroundColor: row.tieneConclusiones ? '#f0fdf4' : '#fef9c3',
                                                color:           row.tieneConclusiones ? '#6f9b19' : '#92400e',
                                                border: `1px solid ${row.tieneConclusiones ? '#bbf7d0' : '#fde68a'}`,
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {row.tieneConclusiones ? '✓ OK' : '⚠ Pendiente'}
                                            </span>
                                            {/* Botón editar/añadir */}
                                            <button
                                                onClick={e => handleAbrirConclusiones(e, row)}
                                                style={{
                                                    padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                                                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                                                    border: `1.5px solid ${row.tieneConclusiones ? '#002b67' : '#6f9b19'}`,
                                                    backgroundColor: row.tieneConclusiones ? '#fff' : '#6f9b19',
                                                    color: row.tieneConclusiones ? '#002b67' : '#fff',
                                                }}
                                                onMouseEnter={e => (e.currentTarget.style.opacity = '0.75')}
                                                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                                            >
                                                {row.tieneConclusiones ? '✏ Editar' : '+ Añadir'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ── Paginación ───────────────────────────────────────────── */}
                <div className="ca-pagination" style={{ padding: '14px 24px', borderTop: '1px solid #f3f4f6' }}>
                    <p className="ca-pag-info">
                        {carregant
                            ? 'Cargando...'
                            : `Página ${pagina} de ${totalPaginas} · ${total} resultados`}
                    </p>
                    <div className="ca-pag-btns">
                        <button
                            className="ca-pag-btn"
                            disabled={pagina === 1 || carregant}
                            onClick={() => setPagina(p => p - 1)}
                        >← Anterior</button>

                        {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                            const start = Math.max(1, Math.min(pagina - 2, totalPaginas - 4));
                            const p = start + i;
                            return p <= totalPaginas ? (
                                <button
                                    key={p}
                                    className={`ca-pag-btn${p === pagina ? ' active' : ''}`}
                                    disabled={carregant}
                                    onClick={() => setPagina(p)}
                                >{p}</button>
                            ) : null;
                        })}

                        <button
                            className="ca-pag-btn"
                            disabled={pagina === totalPaginas || carregant}
                            onClick={() => setPagina(p => p + 1)}
                        >Siguiente →</button>
                    </div>
                </div>
            </div>

            {/* ── Panel conclusiones ───────────────────────────────────────── */}
            {conclusionesOpen && evaluacionSeleccionada && (
                <ConclusionesAdmin
                    evaluacion={evaluacionSeleccionada}
                    tokenJwt={auth.tokenJwt}
                    onCerrar={() => { setConclusionesOpen(false); setEvaluacionSeleccionada(null); }}
                    onGuardado={handleConclusionesGuardadas}
                />
            )}
        </>
    );
};